import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaEye, FaSearch, FaBan, FaCheck, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { fetchDeliveries, toggleDeliveryStatus, toggleDeliveryBan, clearError, clearSuccess } from '../../../redux/Slices/AdminDeliveriesSlice';
import ConfirmModal from '../../../shared/components/ConfirmModal';

const AdminDeliveries = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { deliveries, pagination, loading, updating, error, success } = useSelector((state) => state.adminDeliveries);
    
    const [currentPage, setCurrentPage] = useState(1);
    const [searchName, setSearchName] = useState('');
    const [searchPhone, setSearchPhone] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterBan, setFilterBan] = useState('');
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', data: null });
    
    // Pagination states for dropdowns (if needed in future)
    const [statusPage, setStatusPage] = useState(1);
    const [banPage, setBanPage] = useState(1);
    const [statusPagination, setStatusPagination] = useState({});
    const [banPagination, setBanPagination] = useState({});

    useEffect(() => {
        setTitle('إدارة الديليفري');
    }, [setTitle]);

    useEffect(() => {
        dispatch(fetchDeliveries({ 
            page: currentPage, 
            name: searchName,
            phone: searchPhone,
            status: filterStatus,
            ban: filterBan
        }));
    }, [dispatch, currentPage, searchName, searchPhone, filterStatus, filterBan]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (success) {
            showSuccessToast(success);
            dispatch(clearSuccess());
            dispatch(fetchDeliveries({ 
                page: currentPage, 
                name: searchName,
                phone: searchPhone,
                status: filterStatus,
                ban: filterBan
            }));
        }
    }, [success, dispatch, currentPage, searchName, searchPhone, filterStatus, filterBan]);

    const handleViewDetails = (deliveryId) => {
        navigate(`/admin-deliveries/${deliveryId}`);
    };

    const handleToggleStatus = async (deliveryId, currentStatus) => {
        const newStatus = currentStatus === "1" || currentStatus === 1 ? 0 : 1;
        const confirmMessage = newStatus === 1 ? 'هل أنت متأكد من تفعيل هذا الديليفري؟' : 'هل أنت متأكد من إلغاء تفعيل هذا الديليفري؟';
        
        setConfirmModal({
            isOpen: true,
            type: 'toggleStatus',
            data: { deliveryId, status: newStatus },
            message: confirmMessage,
            title: newStatus === 1 ? 'تأكيد التفعيل' : 'تأكيد إلغاء التفعيل',
            confirmButtonColor: newStatus === 1 ? '#28a745' : '#ffc107'
        });
    };

    const handleToggleBan = async (deliveryId, currentBan) => {
        const newBanStatus = currentBan === "1" || currentBan === 1 ? 0 : 1;
        const confirmMessage = newBanStatus === 1 ? 'هل أنت متأكد من حظر هذا الديليفري؟' : 'هل أنت متأكد من إلغاء حظر هذا الديليفري؟';
        
        setConfirmModal({
            isOpen: true,
            type: 'toggleBan',
            data: { deliveryId, status: newBanStatus },
            message: confirmMessage,
            title: newBanStatus === 1 ? 'تأكيد الحظر' : 'تأكيد إلغاء الحظر',
            confirmButtonColor: newBanStatus === 1 ? '#dc3545' : '#28a745'
        });
    };

    const handleConfirmAction = () => {
        if (confirmModal.type === 'toggleStatus') {
            dispatch(toggleDeliveryStatus(confirmModal.data));
        } else if (confirmModal.type === 'toggleBan') {
            dispatch(toggleDeliveryBan(confirmModal.data));
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchName('');
        setSearchPhone('');
        setFilterStatus('');
        setFilterBan('');
        setCurrentPage(1);
    };

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1) {
            return <span className="badge bg-success text-white">نشط</span>;
        }
        return <span className="badge bg-danger text-white">غير نشط</span>;
    };

    const getBanBadge = (ban) => {
        if (ban === "1" || ban === 1) {
            return <span className="badge bg-danger text-white">محظور</span>;
        }
        return <span className="badge bg-success text-white">غير محظور</span>;
    };

    const renderPagination = () => {
        const totalPages = pagination?.last_page || 1;
        const pages = [];
        
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => setCurrentPage(i)}>
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="Page navigation">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}>
                            السابق
                        </button>
                    </li>
                    {pages}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}>
                            التالي
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إدارة الديليفري</h4>
                </div>

                {/* Search and Filter */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label">البحث بالاسم</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="ابحث بالاسم..."
                                    value={searchName}
                                    onChange={(e) => setSearchName(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">البحث بالهاتف</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="ابحث برقم الهاتف..."
                                    value={searchPhone}
                                    onChange={(e) => setSearchPhone(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">الحالة</label>
                                <select
                                    className="form-select"
                                    value={filterStatus}
                                    onChange={(e) => {
                                        setFilterStatus(e.target.value);
                                        setCurrentPage(1);
                                    }}>
                                    <option value="">الكل</option>
                                    <option value="1">نشط</option>
                                    <option value="0">غير نشط</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label">الحظر</label>
                                <select
                                    className="form-select"
                                    value={filterBan}
                                    onChange={(e) => {
                                        setFilterBan(e.target.value);
                                        setCurrentPage(1);
                                    }}>
                                    <option value="">الكل</option>
                                    <option value="0">غير محظور</option>
                                    <option value="1">محظور</option>
                                </select>
                            </div>
                            <div className="col-md-2 d-flex align-items-end">
                                <button 
                                    className="btn text-white w-100"
                                    style={{ backgroundColor: 'rgb(24, 65, 148)' }}
                                    onClick={handleSearch}>
                                    <FaSearch className="mx-2" />
                                    بحث
                                </button>
                            </div>
                            <div className="col-md-12 d-flex justify-content-end">
                                <button 
                                    className="btn btn-outline-secondary"
                                    onClick={handleClearFilters}>
                                    مسح الفلاتر
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Deliveries Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الديليفري...</p>
                            </div>
                        ) : !Array.isArray(deliveries) || deliveries.length === 0 ? (
                            <div className="text-center py-5">
                                <p className="mt-3 text-muted">لا يوجد ديليفري</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-modern">
                                        <thead>
                                            <tr>
                                                <th className="text-center">#</th>
                                                <th className="text-center">الديليفري</th>
                                                <th className="text-center">رقم الهاتف</th>
                                                <th className="text-center">الحالة</th>
                                                <th className="text-center">الحظر</th>
                                                <th className="text-center">الرصيد</th>
                                                <th className="text-center">الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {deliveries.map((delivery, index) => (
                                                <tr key={delivery.id} className="table-row-modern">
                                                    <td className="text-center">
                                                        <div className="order-number-badge">
                                                            #{(currentPage - 1) * 10 + index + 1}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="delivery-name">
                                                            {delivery.name || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="phone-badge">
                                                            {delivery.phone || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        {getStatusBadge(delivery.status)}
                                                    </td>
                                                    <td className="text-center">
                                                        {getBanBadge(delivery.ban)}
                                                    </td>
                                                    <td className="text-center">
                                                        <div className={`balance-display ${delivery.balance < 0 ? 'negative' : 'positive'}`}>
                                                            {delivery.balance || 0} ج.م
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="action-buttons">
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-view"
                                                                onClick={() => handleViewDetails(delivery.id)}
                                                                title="عرض التفاصيل">
                                                                <FaEye />
                                                            </motion.button>
                                                            {delivery.status === "1" || delivery.status === 1 ? (
                                                                <motion.button
                                                                    whileHover={{ scale: 1.1, y: -2 }}
                                                                    whileTap={{ scale: 0.95 }}
                                                                    className="btn btn-toggle btn-sm"
                                                                    onClick={() => handleToggleStatus(delivery.id, delivery.status)}
                                                                    disabled={updating}
                                                                    title="إلغاء التفعيل">
                                                                    <FaToggleOff />
                                                                </motion.button>
                                                            ) : (
                                                                <motion.button
                                                                    whileHover={{ scale: 1.1, y: -2 }}
                                                                    whileTap={{ scale: 0.95 }}
                                                                    className="btn btn-toggle btn-sm"
                                                                    onClick={() => handleToggleStatus(delivery.id, delivery.status)}
                                                                    disabled={updating}
                                                                    title="تفعيل">
                                                                    <FaToggleOn />
                                                                </motion.button>
                                                            )}
                                                            {delivery.ban === "1" || delivery.ban === 1 ? (
                                                                <motion.button
                                                                    whileHover={{ scale: 1.1, y: -2 }}
                                                                    whileTap={{ scale: 0.95 }}
                                                                    className="btn btn-unban"
                                                                    onClick={() => handleToggleBan(delivery.id, delivery.ban)}
                                                                    disabled={updating}
                                                                    title="إلغاء الحظر">
                                                                    <FaCheck />
                                                                </motion.button>
                                                            ) : (
                                                                <motion.button
                                                                    whileHover={{ scale: 1.1, y: -2 }}
                                                                    whileTap={{ scale: 0.95 }}
                                                                    className="btn btn-ban"
                                                                    onClick={() => handleToggleBan(delivery.id, delivery.ban)}
                                                                    disabled={updating}
                                                                    title="حظر">
                                                                    <FaBan />
                                                                </motion.button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination && renderPagination()}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, type: '', data: null })}
                onConfirm={handleConfirmAction}
                title={confirmModal.title || 'تأكيد العملية'}
                message={confirmModal.message || 'هل أنت متأكد من هذا الإجراء؟'}
                confirmText="تأكيد"
                cancelText="إلغاء"
                confirmButtonColor={confirmModal.confirmButtonColor || '#dc3545'}
                type={confirmModal.type === 'toggleBan' && confirmModal.data?.status === 1 ? 'danger' : 'warning'}
            />
        </div>
    );
};

export default AdminDeliveries;

// CSS للتصميم الحديث
const style = document.createElement('style');
style.textContent = `
  /* تصميم الجدول الحديث */
  .table-modern {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: none;
  }
  
  .table-modern thead {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }
  
  .table-modern thead th {
    border: none;
    padding: 1rem;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .table-modern tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.3s ease;
  }
  
  .table-modern tbody tr:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .table-modern tbody td {
    border: none;
    padding: 1rem;
    vertical-align: middle;
  }
  
  /* تصميم رقم التوصيل */
  .order-number-badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.875rem;
    display: inline-block;
    min-width: 60px;
    text-align: center;
  }
  
  /* تصميم معلومات التوصيل */
  .delivery-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
    justify-content: center;
  }
  
  .delivery-avatar-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  /* أيقونات افتراضية للبيانات الفارغة */
  .default-placeholder {
    width: 50px;
    height: 50px;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 1.2rem;
    transition: all 0.3s ease;
  }
  
  .default-placeholder:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #64748b;
  }
  
  /* تصميم اسم التوصيل */
  .delivery-name {
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
  }
  
  /* تصميم رقم الهاتف */
  .phone-badge {
    background: #f0f9ff;
    color: #0369a1;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    border: 1px solid #bae6fd;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* تصميم الرصيد */
  .balance-display {
    background: #f8fafc;
    color: #64748b;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #e2e8f0;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .balance-display.positive {
    color: #059669;
    background: #f0fdf4;
    border-color: #bbf7d0;
  }
  
  .balance-display.negative {
    color: #dc2626;
    background: #fef2f2;
    border-color: #fecaca;
  }
  
  /* تصميم أزرار الإجراءات */
  .action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-direction: row;
    flex-wrap: nowrap;
  }
  
  .btn-view, .btn-toggle, .btn-ban, .btn-unban {
    border: none !important;
    color: white !important;
    padding: 0 !important;
    border-radius: 8px !important;
    font-size: 0 !important;
    font-weight: 500 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    max-width: 32px !important;
    overflow: hidden !important;
  }
  
  .btn-ban {
    font-size: 1rem;
  }
  
  .btn-view {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  }
  
  .btn-toggle {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
  }
  
  .btn-ban {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
  }
  
  .btn-unban {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
  }
  
  .btn-view:hover, .btn-toggle:hover, .btn-ban:hover, .btn-unban:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  .btn-view *, .btn-toggle *, .btn-ban *, .btn-unban * {
    color: white !important;
    font-size: 0 !important;
    line-height: 0 !important;
    text-indent: -9999px !important;
    overflow: hidden !important;
  }

  .btn-view svg, .btn-toggle svg, .btn-ban svg, .btn-unban svg {
    display: block !important;
    width: 14px !important;
    height: 14px !important;
    margin: 0 !important;
    padding: 0 !important;
    text-indent: 0 !important;
  }
  
  /* تحسين البادج */
  .badge {
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  /* إصلاح الباجينيشن */
  .pagination .page-item.active .page-link {
    background-color: #667eea !important;
    border-color: #667eea !important;
    color: white !important;
  }
  .pagination .page-item .page-link {
    color: #667eea;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin: 0 2px;
    padding: 0.5rem 0.75rem;
  }
  .pagination .page-item .page-link:hover {
    color: #5a67d8;
    background-color: #f8fafc;
    border-color: #cbd5e1;
  }
`;
document.head.appendChild(style);

